import { IconButton, MenuItem, Paper, Popover, Tooltip } from '@mui/material';
import { BlockDomain, BlockStatus, Metric } from '@strength/domain';
import {
  MetricBenchmarkData,
  WorkoutPublishErrors,
  WorkoutSection,
} from '@strength/domain-workout';
import { MdAdd, MdRemove } from '@teamworksdev/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DisplayWorkoutStatus,
  isWorkoutStatusReadOnly,
} from '../../../components/badge/workout-status-badge';
import { ErrorIcon } from '../../../components/icons/error-icon';
import { getSupersetIndexMap } from '../../../components/icons/SupersetIcon';
import { CustomSlot, whitespaceSlot } from '../../../components/sheet/slots';
import { WORKOUT_HEADER_SLOT } from '../../../components/sheet/slots/slot-labels';
import { cn } from '../../../components/utils';
import { useFlags } from '../../../context/featureFlag';
import { jotaiStore } from '../../../utils/jotai';
import { ApplySwapCallbacks } from '../components/apply-swap-drawer';
import { AutoFixButton } from '../components/autofix-button';
import { ExerciseHeight, getYs } from '../grid-utils';
import {
  MiscMutationFns,
  WorkoutLayerMutationFns,
} from '../mutation-fns/types';
import { WorkoutPublishErrorsById } from '../publish/utils';
import type {
  AddRemoveSession,
  BlockSlot,
  BlockView,
  SortedWorkout,
  WorkoutWithCycleAndSession,
} from '../types';
import {
  exercisesForSection,
  getDisplayNameForWorkout,
  isLastExerciseInGroup,
} from '../utils';
import {
  collapsedExercisesAtom,
  collapsedSectionsAtom,
  getKeyForExerciseCollapse,
  getKeyForSectionCollapse,
  inputAtom,
  isExerciseHidden,
} from './common';
import { datePickerSlot } from './date';
import {
  exerciseColumnCount,
  getAddExerciseSlot,
  getExerciseSlots,
} from './exercises';
import { getAddSectionSlot, getSectionSlots } from './sections';
import { workoutNameSlot } from './workoutName';

type GetSlotsForBlockProps = {
  workouts: WorkoutWithCycleAndSession[];
  allErrors: WorkoutPublishErrorsById[];
  view: BlockView;
  mutationFns: WorkoutLayerMutationFns;
  miscMutationFns: MiscMutationFns;
  applyBenchmarkData?: MetricBenchmarkData;
  setModalOrDrawerOpen: (open: boolean) => void;
  hasBlockBeenPublished: boolean;
  isPublishDisabled: boolean;
  blockStatus: BlockStatus;
  allowFlexWorkouts: boolean;
  getMetric: (id: string) => Metric;
  maxSetsColumns: number;
  maxNonSetsColumns: number;
  greatestExerciseHeightsInSections: Array<Array<ExerciseHeight>>;
  getAssignedDate: (workout: WorkoutWithCycleAndSession) => string | null;
  selectedWorkoutId?: string;
  selectedAthleteId?: string;
  showActuals?: boolean;
  applySwapCallbacks?: ApplySwapCallbacks;
  forceReadOnly?: boolean;
  /**
   * Omit the workout label cell (session name + add/remove-session controls).
   * Used by the All-view edit panel, which edits a single workout and has no
   * session-management affordances. Cycle view only.
   */
  hideWorkoutLabel?: boolean;
};

export type WorkoutColumnInfo = {
  setsColumnCount: number;
  nonSetsColumnCount: number;
  workoutStartColumn: number;
  isReadOnly?: boolean;
  isAthleteSheet?: boolean;
  shouldShowSetsColumn?: boolean;
};

export function getSlotsForBlock({
  workouts,
  allErrors,
  view,
  mutationFns,
  miscMutationFns,
  applyBenchmarkData,
  setModalOrDrawerOpen,
  hasBlockBeenPublished,
  isPublishDisabled,
  blockStatus,
  allowFlexWorkouts,
  getMetric,
  selectedWorkoutId,
  selectedAthleteId,
  showActuals,
  maxSetsColumns,
  maxNonSetsColumns,
  greatestExerciseHeightsInSections,
  getAssignedDate,
  applySwapCallbacks,
  forceReadOnly,
  hideWorkoutLabel,
}: GetSlotsForBlockProps): {
  slots: BlockSlot[];
  rowCount: number;
  columnCount: number;
} {
  const displayWorkouts = workouts.flatMap((w) => {
    const useWorkout =
      view.mode === 'session'
        ? w.session.id === view.sessionId
        : w.cycle.id === view.cycleId;

    if (useWorkout) {
      return {
        ...w,
        // Force read-only when showActuals is enabled (review mode)
        isReadOnly:
          forceReadOnly ||
          showActuals ||
          checkIfReadOnly(w, blockStatus),
      };
    } else {
      return [];
    }
  });

  const sortedWorkouts = displayWorkouts.sort((a, b) => {
    if (view.mode === 'session') {
      return a.cycle.startDate.localeCompare(b.cycle.startDate);
    } else {
      if (a.session.id === b.session.id) {
        // if we have 2 sessions with the same id, then one is an athlete specific workout
        // and these should always come first in the order
        // there is an assumption here that we'll never have 2 identical sessions that are
        // both athlete specific
        return a.isAthleteOnlyLayer ? -1 : 1;
      }
      return a.session.id - b.session.id;
    }
  });

  if (view.mode === 'session') {
    return getSlotsForBlock_SessionView({
      sortedWorkouts,
      allErrors,
      mutationFns,
      miscMutationFns,
      applyBenchmarkData,
      applySwapCallbacks,
      setModalOrDrawerOpen,
      isPublishDisabled,
      blockStatus,
      allowFlexWorkouts,
      getMetric,
      selectedWorkoutId,
      selectedAthleteId,
      showActuals,
      maxSetsColumns,
      maxNonSetsColumns,
      greatestExerciseHeightsInSections,
      getAssignedDate,
    });
  } else {
    return getSlotsForBlock_CycleView({
      sortedWorkouts,
      allErrors,
      mutationFns,
      miscMutationFns,
      applyBenchmarkData,
      applySwapCallbacks,
      setModalOrDrawerOpen,
      hasBlockBeenPublished,
      isPublishDisabled,
      blockStatus,
      allowFlexWorkouts,
      getMetric,
      selectedWorkoutId,
      selectedAthleteId,
      showActuals,
      maxSetsColumns,
      maxNonSetsColumns,
      getAssignedDate,
      hideWorkoutLabel,
    });
  }
}

export const checkIfReadOnly = (
  workout: WorkoutWithCycleAndSession,
  blockStatus: BlockStatus,
): boolean => {
  const { status } = workout;
  if (
    (status.type === 'completed' || status.type === 'in-progress') &&
    status.readOnly !== undefined
  ) {
    // The readonly state has already been calculated
    return status.readOnly;
  }

  // if the block is canceled, then all workouts are read only
  if (blockStatus === 'canceled') {
    return true;
  }

  if (isWorkoutStatusReadOnly(workout.status)) {
    return true;
  }
  // For athlete workouts, actuals no longer make it read-only (allows editing in-progress workouts)
  return false;
};

// Utility function to calculate grid styles for a workout
const getWorkoutGridStyle = (
  currentX: number,
  uniformWorkoutWidth: number,
  totalWorkouts: number,
): {
  style: React.CSSProperties;
  overrideGridStyles: boolean;
} => {
  // When there's only one workout, it should span the entire grid width
  const shouldSpanFullWidth = totalWorkouts === 1;
  return {
    style: {
      gridColumnStart: currentX + 1, // CSS grid is 1-indexed
      gridColumnEnd: shouldSpanFullWidth ? '-1' : `span ${uniformWorkoutWidth}`,
    },
    overrideGridStyles: true,
  };
};

// This lines up each section and exercise by y-coordinate, which requires extra work compared to the week view
function getSlotsForBlock_SessionView({
  sortedWorkouts,
  allErrors,
  mutationFns,
  miscMutationFns,
  applyBenchmarkData,
  applySwapCallbacks,
  setModalOrDrawerOpen,
  isPublishDisabled,
  blockStatus,
  allowFlexWorkouts,
  getMetric,
  selectedWorkoutId,
  selectedAthleteId,
  showActuals,
  maxSetsColumns,
  maxNonSetsColumns,
  greatestExerciseHeightsInSections,
  getAssignedDate,
}: {
  sortedWorkouts: SortedWorkout[];
  allErrors: WorkoutPublishErrorsById[];
  mutationFns: WorkoutLayerMutationFns;
  miscMutationFns: MiscMutationFns;
  applyBenchmarkData?: MetricBenchmarkData;
  applySwapCallbacks?: ApplySwapCallbacks;
  setModalOrDrawerOpen: (open: boolean) => void;
  isPublishDisabled: boolean;
  blockStatus: BlockStatus;
  allowFlexWorkouts: boolean;
  getMetric: (id: string) => Metric;
  maxSetsColumns: number;
  maxNonSetsColumns: number;
  // session view has more complicated Y positioning logic because the exercies and sections are lined up
  greatestExerciseHeightsInSections: Array<Array<ExerciseHeight>>;
  getAssignedDate: (workout: WorkoutWithCycleAndSession) => string | null;
  selectedWorkoutId?: string;
  selectedAthleteId?: string;
  showActuals?: boolean;
}): {
  slots: BlockSlot[];
  rowCount: number;
  columnCount: number;
} {
  const slots: BlockSlot[] = [];
  const collapsedSections = jotaiStore.get(collapsedSectionsAtom);
  const collapsedExercises = jotaiStore.get(collapsedExercisesAtom);
  const { maxY, sectionAndExerciseYs } = getYs(
    greatestExerciseHeightsInSections,
    sortedWorkouts,
    collapsedSections,
    selectedAthleteId ?? null,
  );
  // Use sets columns + non sets columns to calculate the uniform workout width
  let currentX = 0;

  for (const workout of sortedWorkouts) {
    // Hide Sets column when showing actuals (expanded format doesn't have Sets metric)
    const shouldShowSetsColumn = !workout.isReadOnly && !showActuals;
    const uniformWorkoutWidth =
      (shouldShowSetsColumn ? 1 : 0) + maxNonSetsColumns;
    // Get computed grid style for this workout
    const gridStyle = getWorkoutGridStyle(
      currentX,
      uniformWorkoutWidth,
      sortedWorkouts.length,
    );
    const workoutName = workout.name ?? getDisplayNameForWorkout(workout);
    const isReadOnly = workout.isReadOnly;
    const isCancelDisabled = isCancelDisabledForWorkout(
      workout.status,
      isPublishDisabled,
    );
    const highlighted = workout.workoutId === selectedWorkoutId;
    const actuals = showActuals
      ? workout.actual?.find(
          (l) =>
            workout.workoutId === l.workoutId &&
            l.profileId === selectedAthleteId,
        )
      : undefined;

    const errors =
      allErrors.find((e) => e.workoutId === workout.workoutId)?.errors ?? null;

    slots.push(
      new CustomSlot(
        { x: currentX, y: 0 },
        uniformWorkoutWidth,
        1,
        isReadOnly,
        () => (
          <WorkoutLabel
            displayName="cycle"
            workout={workout}
            hasBlockBeenPublished={false}
            isCancelDisabled={isCancelDisabled}
            errors={errors}
            sessionCount={0}
            onCancelWorkout={
              isReadOnly ? undefined : miscMutationFns.onCancelWorkout
            }
            workoutName={workoutName}
            profileId={selectedAthleteId}
          />
        ),
        {
          backgroundClassName: highlighted
            ? 'bg-informational-25'
            : 'bg-gray-200',
          data: [
            {
              type: 'Workout',
              workoutId: workout.workoutId,
              workout,
              sections: workout.sections,
              disabled: isReadOnly,
            },
          ],
          style: gridStyle.style,
          overrideGridStyles: gridStyle.overrideGridStyles,
        },
        WORKOUT_HEADER_SLOT,
      ),
    );

    slots.push(
      workoutNameSlot({
        x: currentX,
        y: 1,
        width: uniformWorkoutWidth,
        height: 1,
        value: workout.name,
        workout,
        inputAtom,
        disabled: isReadOnly,
        onValueChange: (value: string | null) =>
          miscMutationFns.onEditWorkoutName?.(workout.workoutId, value),
        gridOptions: gridStyle,
      }),
    );

    slots.push(
      datePickerSlot({
        startDate: workout.cycle.startDate,
        endDate: workout.cycle.endDate,
        x: currentX,
        y: 2,
        width: uniformWorkoutWidth,
        height: 1,
        value: getAssignedDate(workout) ?? workout.assignedDate,
        inputAtom,
        disabled: !miscMutationFns.onEditWorkoutDate || isReadOnly,
        error: !!errors?.get('assignedDate'),
        workoutStatus: workout.status,
        allowFlexWorkouts,
        blockStatus,
        onValueChange: (value) =>
          miscMutationFns.onEditWorkoutDate?.(workout.workoutId, value),
        gridOptions: gridStyle,
      }),
    );

    // Compute superset index map once per workout
    const supersetIndexMap = getSupersetIndexMap(workout.sections);

    let sectionIndex = 0;
    for (const section of workout.sections) {
      const sectionKey = getKeyForSectionCollapse(
        section,
        selectedAthleteId ?? null,
      );
      const ys = sectionAndExerciseYs[sectionIndex];
      if (!ys) {
        continue;
      }
      const { sectionData, exercises } = ys;
      const { slots: sectionSlots, rowCount: sectionRowCount } =
        getSectionSlots(
          workout,
          section,
          sectionKey,
          errors,
          currentX,
          sectionData.y,
          uniformWorkoutWidth,
          isReadOnly,
          mutationFns,
          gridStyle,
        );
      slots.push(...sectionSlots);

      // Check if section is collapsed
      const isSectionCollapsed = collapsedSections.has(sectionKey);

      let exerciseIndex = 0;
      for (const { exercise, superset, alternateGroup } of exercisesForSection(
        section,
      )) {
        // Skip exercises if section is collapsed
        if (
          isSectionCollapsed ||
          isExerciseHidden(
            isSectionCollapsed,
            exercise,
            alternateGroup,
            selectedAthleteId ?? null,
            collapsedExercises,
          )
        ) {
          exerciseIndex++;
          continue;
        }
        const exerciseData = exercises[exerciseIndex];
        if (!exerciseData) {
          continue;
        }

        const exerciseKey = getKeyForExerciseCollapse(
          exercise,
          selectedAthleteId ?? null,
        );
        const isExerciseCollapsed = collapsedExercises.has(exerciseKey);
        const exerciseWorkoutColumnInfo = {
          setsColumnCount: maxSetsColumns,
          nonSetsColumnCount: maxNonSetsColumns,
          workoutStartColumn: currentX,
          isReadOnly: isReadOnly,
          isAthleteSheet: selectedAthleteId !== undefined,
          shouldShowSetsColumn: shouldShowSetsColumn,
        };

        const { slots: exerciseSlots, rowCount: exerciseRowCount } =
          getExerciseSlots(
            section,
            superset,
            alternateGroup,
            exercise,
            isExerciseCollapsed,
            exerciseKey,
            errors,
            currentX,
            exerciseData.y,
            uniformWorkoutWidth,
            isReadOnly,
            mutationFns,
            miscMutationFns,
            applyBenchmarkData,
            setModalOrDrawerOpen,
            getMetric,
            actuals,
            exerciseWorkoutColumnInfo,
            gridStyle,
            showActuals,
            supersetIndexMap,
            applySwapCallbacks,
          );
        slots.push(...exerciseSlots);

        if (exerciseData.height > exerciseRowCount) {
          slots.push(
            whitespaceSlot(
              currentX,
              exerciseData.y + exerciseRowCount,
              uniformWorkoutWidth,
              exerciseData.height - exerciseRowCount,
              gridStyle,
            ),
          );
        }

        exerciseIndex += 1;
      }

      const addExerciseRowHeight = 1;

      // when not collapsed, the last slot is the last exercise
      // else it is just after the section header
      const lastExerciseData = exercises[exerciseIndex - 1];
      const lastSlotY: number =
        lastExerciseData && !isSectionCollapsed
          ? lastExerciseData.y + lastExerciseData.height
          : sectionData.y + sectionRowCount;

      let addedAddExerciseSlot = false;
      if (!isReadOnly && !isSectionCollapsed) {
        // when not collapsed and not read only, add the add exercise slot after the last exercise
        const { slots: addExerciseSlot } = getAddExerciseSlot(
          section,
          currentX,
          lastSlotY,
          uniformWorkoutWidth,
          isReadOnly,
          mutationFns,
          setModalOrDrawerOpen,
          undefined, // altGroupId
          gridStyle,
        );
        slots.push(...addExerciseSlot);
        addedAddExerciseSlot = true;
      }

      const lastSectionY = sectionData.y + sectionData.height;
      // Only add addExerciseRowHeight if we actually added an add exercise slot or whitespace for it
      const lastY =
        lastSlotY + (addedAddExerciseSlot ? addExerciseRowHeight : 0);
      if (lastSectionY > lastY) {
        slots.push(
          whitespaceSlot(
            currentX,
            lastY,
            uniformWorkoutWidth,
            lastSectionY - lastY,
            gridStyle,
          ),
        );
      }

      sectionIndex += 1;
    }

    const lastSectionYs = sectionAndExerciseYs[sectionIndex - 1];
    const addSectionY = lastSectionYs
      ? lastSectionYs.sectionData.y + lastSectionYs.sectionData.height
      : 3; // Account for workout name (y:1) and assigned date (y:2) slots
    const addSectionRowHeight = 1;
    if (!isReadOnly) {
      const { slots: addSectionSlots } = getAddSectionSlot(
        workout,
        workoutName,
        currentX,
        addSectionY,
        uniformWorkoutWidth,
        mutationFns,
        miscMutationFns.onSelectWorkoutTemplate,
        setModalOrDrawerOpen,
        gridStyle,
      );
      slots.push(...addSectionSlots);
    } else {
      slots.push(
        whitespaceSlot(
          currentX,
          addSectionY,
          uniformWorkoutWidth,
          addSectionRowHeight,
          gridStyle,
        ),
      );
    }

    const lastY = addSectionY + addSectionRowHeight;
    if (maxY > lastY) {
      slots.push(
        whitespaceSlot(
          currentX,
          lastY,
          uniformWorkoutWidth,
          maxY - lastY,
          gridStyle,
        ),
      );
    }

    currentX += uniformWorkoutWidth;
  }

  return {
    slots,
    rowCount: maxY,
    columnCount: currentX,
  };
}

function getSlotsForBlock_CycleView({
  sortedWorkouts,
  allErrors,
  mutationFns,
  miscMutationFns,
  setModalOrDrawerOpen,
  applyBenchmarkData,
  applySwapCallbacks,
  hasBlockBeenPublished,
  isPublishDisabled,
  blockStatus,
  allowFlexWorkouts,
  getMetric,
  selectedWorkoutId,
  selectedAthleteId,
  showActuals,
  maxSetsColumns,
  maxNonSetsColumns,
  getAssignedDate,
  hideWorkoutLabel,
}: {
  sortedWorkouts: SortedWorkout[];
  allErrors: WorkoutPublishErrorsById[];
  mutationFns: WorkoutLayerMutationFns;
  miscMutationFns: MiscMutationFns;
  setModalOrDrawerOpen: (open: boolean) => void;
  applyBenchmarkData?: MetricBenchmarkData;
  applySwapCallbacks?: ApplySwapCallbacks;
  hasBlockBeenPublished: boolean;
  isPublishDisabled: boolean;
  blockStatus: BlockStatus;
  allowFlexWorkouts: boolean;
  getMetric: (id: string) => Metric;
  maxSetsColumns: number;
  maxNonSetsColumns: number;
  selectedWorkoutId?: string;
  selectedAthleteId?: string;
  showActuals?: boolean;
  getAssignedDate: (workout: WorkoutWithCycleAndSession) => string | null;
  hideWorkoutLabel?: boolean;
}): {
  slots: BlockSlot[];
  rowCount: number;
  columnCount: number;
} {
  const slots: BlockSlot[] = [];
  const collapsedSections = jotaiStore.get(collapsedSectionsAtom);
  const collapsedExercises = jotaiStore.get(collapsedExercisesAtom);

  let currentX = 0;
  // The panel hides the workout label cell; drop that row from the grid
  // height so no empty row is left at the top of the sheet.
  const hiddenHeaderRows = hideWorkoutLabel ? 1 : 0;
  const maxY = Math.max(
    0,
    sortedWorkouts.reduce((max, s) => Math.max(max, workoutColumnCount(s)), 0) -
      hiddenHeaderRows,
  );

  for (const workout of sortedWorkouts) {
    // Hide Sets column when showing actuals (expanded format doesn't have Sets metric)
    const shouldShowSetsColumn = !workout.isReadOnly && !showActuals;
    const uniformWorkoutWidthCycle =
      (shouldShowSetsColumn ? 1 : 0) + maxNonSetsColumns;
    // Get computed grid style for this workout
    const gridStyleCycle = getWorkoutGridStyle(
      currentX,
      uniformWorkoutWidthCycle,
      sortedWorkouts.length,
    );

    const workoutName = workout.name ?? getDisplayNameForWorkout(workout);
    const isReadOnly = workout.isReadOnly;
    const isCancelDisabled = isCancelDisabledForWorkout(
      workout.status,
      isPublishDisabled,
    );
    const highlighted = workout.workoutId === selectedWorkoutId;
    const actuals = showActuals
      ? workout.actual?.find(
          (l) =>
            workout.workoutId === l.workoutId &&
            l.profileId === selectedAthleteId,
        )
      : undefined;

    const errors =
      allErrors.find((e) => e.workoutId === workout.workoutId)?.errors ?? null;
    let currentY = 0;
    if (!hideWorkoutLabel) {
      slots.push(
        new CustomSlot(
          { x: currentX, y: currentY },
          uniformWorkoutWidthCycle,
          1,
          isReadOnly,
          () => (
            <WorkoutLabel
              displayName="session"
              workout={workout}
              hasBlockBeenPublished={hasBlockBeenPublished}
              isCancelDisabled={isCancelDisabled}
              errors={errors}
              sessionCount={sortedWorkouts.length}
              onAddSession={
                isReadOnly ? undefined : miscMutationFns.onAddSession
              }
              onRemoveSession={
                isReadOnly ? undefined : miscMutationFns.onRemoveSession
              }
              onCancelWorkout={
                isReadOnly ? undefined : miscMutationFns.onCancelWorkout
              }
              workoutName={workoutName}
              profileId={selectedAthleteId}
            />
          ),
          {
            backgroundClassName: highlighted
              ? 'bg-informational-25'
              : 'bg-gray-200',
            data: [
              {
                type: 'Workout',
                workout,
                sections: workout.sections,
                workoutId: workout.workoutId,
                disabled: isReadOnly,
              },
            ],
            style: gridStyleCycle.style,
            overrideGridStyles: gridStyleCycle.overrideGridStyles,
          },
        ),
      );
      currentY += 1;
    }

    slots.push(
      workoutNameSlot({
        x: currentX,
        y: currentY,
        width: uniformWorkoutWidthCycle,
        height: 1,
        value: workout.name,
        workout,
        inputAtom,
        disabled: isReadOnly,
        onValueChange: (value: string | null) =>
          miscMutationFns.onEditWorkoutName?.(workout.workoutId, value),
        gridOptions: gridStyleCycle,
      }),
    );
    currentY += 1;

    slots.push(
      datePickerSlot({
        startDate: workout.cycle.startDate,
        endDate: workout.cycle.endDate,
        x: currentX,
        y: currentY,
        width: uniformWorkoutWidthCycle,
        height: 1,
        value: getAssignedDate(workout) ?? workout.assignedDate,
        inputAtom,
        disabled: !miscMutationFns.onEditWorkoutDate || isReadOnly,
        workoutStatus: workout.status,
        blockStatus,
        allowFlexWorkouts,
        onValueChange: (value) =>
          miscMutationFns.onEditWorkoutDate?.(workout.workoutId, value),
        gridOptions: gridStyleCycle,
      }),
    );
    currentY += 1;

    // Compute superset index map once per workout
    const supersetIndexMap = getSupersetIndexMap(workout.sections);

    for (const section of workout.sections) {
      const sectionKey = getKeyForSectionCollapse(
        section,
        selectedAthleteId ?? null,
      );
      const { slots: sectionSlots, rowCount: sectionRowCount } =
        getSectionSlots(
          workout,
          section,
          sectionKey,
          errors,
          currentX,
          currentY,
          uniformWorkoutWidthCycle,
          isReadOnly,
          mutationFns,
          gridStyleCycle,
        );
      slots.push(...sectionSlots);
      currentY += sectionRowCount;

      // Check if section is collapsed
      const isSectionCollapsed = collapsedSections.has(sectionKey);

      for (const { exercise, superset, alternateGroup } of exercisesForSection(
        section,
      )) {
        // Skip exercise if section is collapsed or
        // it is an alternate exercise in a collapsed alternate group
        if (
          isSectionCollapsed ||
          isExerciseHidden(
            isSectionCollapsed,
            exercise,
            alternateGroup,
            selectedAthleteId ?? null,
            collapsedExercises,
          )
        ) {
          continue;
        }
        const exerciseKey = getKeyForExerciseCollapse(
          exercise,
          selectedAthleteId ?? null,
        );
        const isExerciseCollapsed = collapsedExercises.has(exerciseKey);
        const exerciseWorkoutColumnInfo = {
          setsColumnCount: maxSetsColumns,
          nonSetsColumnCount: maxNonSetsColumns,
          workoutStartColumn: currentX,
          isReadOnly: isReadOnly,
          isAthleteSheet: selectedAthleteId !== undefined,
          shouldShowSetsColumn: shouldShowSetsColumn,
        };

        const { slots: exerciseSlots, rowCount: exerciseHeight } =
          getExerciseSlots(
            section,
            superset,
            alternateGroup,
            exercise,
            isExerciseCollapsed,
            exerciseKey,
            errors,
            currentX,
            currentY,
            uniformWorkoutWidthCycle,
            isReadOnly,
            mutationFns,
            miscMutationFns,
            applyBenchmarkData,
            setModalOrDrawerOpen,
            getMetric,
            actuals,
            exerciseWorkoutColumnInfo,
            gridStyleCycle,
            showActuals,
            supersetIndexMap,
            applySwapCallbacks,
          );
        slots.push(...exerciseSlots);
        currentY += exerciseHeight;
      }

      // 'Add Exercise' Slot
      if (!isSectionCollapsed && !isReadOnly) {
        const { slots: addExerciseSlot, rowCount: addExerciseRowCount } =
          getAddExerciseSlot(
            section,
            currentX,
            currentY,
            uniformWorkoutWidthCycle,
            isReadOnly,
            mutationFns,
            setModalOrDrawerOpen,
            undefined, // altGroupId
            gridStyleCycle,
          );
        slots.push(...addExerciseSlot);
        currentY += addExerciseRowCount;
      }
    }

    // Always add the Add Section button at the bottom of each workout's content
    const { slots: addSectionSlots, rowCount: addSectionRowCount } =
      getAddSectionSlot(
        workout,
        workoutName,
        currentX,
        currentY,
        uniformWorkoutWidthCycle,
        mutationFns,
        miscMutationFns.onSelectWorkoutTemplate,
        setModalOrDrawerOpen,
        gridStyleCycle,
      );
    if (!isReadOnly) {
      slots.push(...addSectionSlots);
      currentY += addSectionRowCount;
    }

    // Add whitespace to match the maxY height, but ensure the Add Section button remains visible
    if (maxY > currentY) {
      slots.push(
        whitespaceSlot(
          currentX,
          currentY,
          uniformWorkoutWidthCycle,
          maxY - currentY,
          gridStyleCycle,
        ),
      );
    }

    currentX += uniformWorkoutWidthCycle;
  }

  return {
    slots,
    rowCount: maxY,
    columnCount: currentX,
  };
}

export const WorkoutLabel = ({
  workout,
  hasBlockBeenPublished,
  displayName,
  errors,
  sessionCount,
  isCancelDisabled,
  onAddSession,
  onRemoveSession,
  onCancelWorkout,
  workoutName,
  profileId,
}: {
  workout: WorkoutWithCycleAndSession;
  hasBlockBeenPublished: boolean;
  displayName: 'session' | 'cycle';
  errors: WorkoutPublishErrors | null;
  sessionCount: number;
  isCancelDisabled: boolean;
  onAddSession?: MiscMutationFns['onAddSession'];
  onRemoveSession?: MiscMutationFns['onRemoveSession'];
  onCancelWorkout?: MiscMutationFns['onCancelWorkout'];
  workoutName?: string;
  profileId?: string;
}) => {
  const { t } = useTranslation();
  const { showWorkoutSupportButton } = useFlags();
  const hasAnyErrors = !!errors;
  const messages =
    errors?.get(workout.workoutId)?.map((err) => t(...err)) ?? [];
  const errorsOnLabel = messages.length > 0;
  const displayText = workout[displayName]?.name;

  function handleAddRemove(selection: 'add-left' | 'add-right' | 'remove') {
    if (selection === 'remove') {
      onRemoveSession?.(workout.cycle.id, workout.session.id);
    } else {
      onAddSession?.(workout.cycle.id, workout.session.id, selection);
    }
  }

  const content = (
    <>
      <div
        className={cn(
          'flex justify-between items-center w-full px-2.5 h-full',
          errorsOnLabel && 'bg-red-100',
        )}
      >
        {displayText}
        <div className="flex items-center gap-1">
          {hasAnyErrors && <ErrorIcon />}
          {showWorkoutSupportButton && (
            <AutoFixButton
              workoutId={workout.workoutId}
              workoutName={workoutName}
              profileId={profileId}
            />
          )}
          <WorkoutModificationButtons
            workout={workout}
            hasBlockBeenPublished={hasBlockBeenPublished}
            onAddRemove={handleAddRemove}
            onCancel={() => onCancelWorkout?.(workout.workoutId)}
            sessionCount={sessionCount}
            displayName={displayName}
            isCancelDisabled={isCancelDisabled}
          />
        </div>
      </div>
    </>
  );

  if (errorsOnLabel) {
    return (
      <Tooltip title={messages.join(', ')} arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

function WorkoutModificationButtons({
  workout,
  sessionCount,
  displayName,
  hasBlockBeenPublished,
  isCancelDisabled,
  onAddRemove,
  onCancel,
}: {
  workout: WorkoutWithCycleAndSession;
  sessionCount: number;
  displayName: 'session' | 'cycle';
  hasBlockBeenPublished: boolean;
  isCancelDisabled: boolean;
  onAddRemove: (selection: AddRemoveSession) => void;
  onCancel: () => void;
}) {
  const [addAnchorEl, setAddAnchorEl] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();

  const { status } = workout;

  // Only show the add/remove workout buttons if we are in the cycle view
  const showAddRemoveWorkoutButtons = displayName === 'session';
  const showDeleteButton =
    showAddRemoveWorkoutButtons && sessionCount > 1 && !hasBlockBeenPublished;
  const showAddButton =
    showAddRemoveWorkoutButtons &&
    sessionCount < BlockDomain.DEFAULT_CYCLE_LENGTH_IN_DAYS;

  const showCancelButton =
    !isCancelDisabled && status.type !== 'draft' && status.type !== 'canceled';

  function handleAddRemove(selection: AddRemoveSession) {
    setAddAnchorEl(null);
    onAddRemove(selection);
  }

  return (
    <>
      {showDeleteButton && (
        <IconButton
          size="small"
          title=""
          onClick={() => handleAddRemove('remove')}
        >
          <MdRemove />
        </IconButton>
      )}
      {showCancelButton && (
        <IconButton size="small" title="" onClick={onCancel}>
          <MdRemove />
        </IconButton>
      )}

      {showAddButton && (
        <IconButton
          size="small"
          title={t('addWorkout')}
          onClick={(e) => setAddAnchorEl(e.currentTarget)}
        >
          <MdAdd />
        </IconButton>
      )}

      <Popover
        anchorEl={addAnchorEl}
        open={Boolean(addAnchorEl)}
        onClose={() => setAddAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        className="absolute z-50"
      >
        <Paper className="p-2">
          <MenuItem onClick={() => handleAddRemove('add-left')}>
            Add Workout to Left
          </MenuItem>
          <MenuItem onClick={() => handleAddRemove('add-right')}>
            Add Workout to Right
          </MenuItem>
        </Paper>
      </Popover>
    </>
  );
}

export function sectionColumnCount(
  section: WorkoutSection,
  isReadOnly: boolean,
) {
  let total = isReadOnly ? 1 : 2; // Section name and Add Exercise (no Add Exercise in read only)
  for (const { exercise, alternateGroup } of exercisesForSection(section)) {
    // name + metics + sets + alternate exercise button if it's the last exercise in the group
    const hasAlternateGroup = isLastExerciseInGroup(
      alternateGroup,
      exercise.workoutExerciseId,
    );
    total += exerciseColumnCount(exercise, isReadOnly, hasAlternateGroup);
  }
  return total;
}

function workoutColumnCount({ isReadOnly, sections }: SortedWorkout) {
  let total = isReadOnly ? 3 : 4; // Session name, workout label, and Add Section (no Add Section in read only)
  for (const section of sections) {
    total += sectionColumnCount(section, isReadOnly);
  }
  return total;
}

function isCancelDisabledForWorkout(
  status: DisplayWorkoutStatus,
  isPublishDisabled: boolean,
) {
  const statusType = status.type;
  return (
    isPublishDisabled ||
    (statusType === 'completed' && status.completed === status.toComplete) ||
    statusType === 'canceled' ||
    statusType === 'in-progress' ||
    statusType === 'skipped'
  );
}
